import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { KeyRound, Plus, Pencil, Trash2, X, Loader2 } from "lucide-react";

const inp = "w-full h-11 px-3 border border-stone-300 text-sm";

export default function ClientLogins() {
  const [logins, setLogins] = useState(null);
  const [ranches, setRanches] = useState([]);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | login object
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", username: "", password: "", client_id: "", block_ids: [] });
  const [q, setQ] = useState("");

  const load = async () => {
    const [l, r, c] = await Promise.all([api.get("/portal/logins"), api.get("/ranches"), api.get("/clients")]);
    setLogins(l.data); setRanches(r.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const byRanch = useMemo(() => {
    const m = new Map();
    ranches.forEach((r) => {
      const k = `${r.ranch_number} · ${r.name}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    });
    return [...m.entries()];
  }, [ranches]);

  const openNew = () => { setF({ name: "", username: "", password: "", client_id: "", block_ids: [] }); setErr(""); setEditing({}); };
  const openEdit = (u) => { setF({ name: u.name || "", username: u.username || "", password: "", client_id: u.client_id || "", block_ids: u.block_ids || [] }); setErr(""); setEditing(u); };
  const toggle = (id) => setF((s) => ({ ...s, block_ids: s.block_ids.includes(id) ? s.block_ids.filter((x) => x !== id) : [...s.block_ids, id] }));
  const toggleRanch = (blocks) => setF((s) => {
    const ids = blocks.map((b) => b.id);
    const allOn = ids.every((id) => s.block_ids.includes(id));
    return { ...s, block_ids: allOn ? s.block_ids.filter((x) => !ids.includes(x)) : [...new Set([...s.block_ids, ...ids])] };
  });

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = { name: f.name, username: f.username, password: f.password || null, client_id: f.client_id || null, block_ids: f.block_ids };
      if (editing?.id) await api.put(`/portal/logins/${editing.id}`, body);
      else await api.post("/portal/logins", body);
      setEditing(null);
      await load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete login for ${u.name}?`)) return;
    await api.delete(`/users/${u.id}`);
    await load();
  };

  const blockLabel = (id) => {
    const r = ranches.find((x) => x.id === id);
    return r ? `${r.ranch_number}·${r.block || r.name}` : id;
  };

  if (!logins) return <div className="p-8 font-bold uppercase text-sm">Loading client logins…</div>;

  return (
    <div className="space-y-5" data-testid="client-logins-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Client Logins</h1>
          <p className="text-stone-500 text-sm mt-1">Read-only portal access for owners &amp; winemakers — scoped to the exact blocks you choose.</p>
        </div>
        <button data-testid="new-client-login-btn" onClick={openNew} className="h-11 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2"><Plus size={14} /> New Client Login</button>
      </div>

      <div className="space-y-2">
        {logins.map((u) => (
          <div key={u.id} data-testid={`client-login-${u.username}`} className="bg-white border border-stone-200 p-4 flex flex-wrap items-center gap-3">
            <KeyRound size={18} className="text-[#7C9673] shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-bold">{u.name} <span className="font-mono font-normal text-sm text-stone-500">· {u.username}</span>
                {u.client_id && <span className="text-xs text-stone-500"> · {clients.find((c) => c.id === u.client_id)?.name || ""}</span>}
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {(u.block_ids || []).map((id) => (
                  <span key={id} className="text-[10px] font-semibold bg-stone-100 border border-stone-200 px-1.5 py-0.5">{blockLabel(id)}</span>
                ))}
                {(u.block_ids || []).length === 0 && <span className="text-xs text-amber-700 font-semibold">No blocks assigned — client will see nothing</span>}
              </div>
            </div>
            <button data-testid={`edit-client-login-${u.username}`} onClick={() => openEdit(u)} className="h-10 w-10 border border-stone-300 flex items-center justify-center"><Pencil size={14} /></button>
            <button data-testid={`delete-client-login-${u.username}`} onClick={() => remove(u)} className="h-10 w-10 border border-stone-300 flex items-center justify-center text-red-700"><Trash2 size={14} /></button>
          </div>
        ))}
        {logins.length === 0 && (
          <div className="bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm" data-testid="client-logins-empty">No client logins yet</div>
        )}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="client-login-modal">
          <div className="bg-white w-full max-w-2xl p-5 space-y-4 my-8">
            <div className="flex items-center justify-between">
              <div className="font-bold uppercase tracking-wide">{editing.id ? "Edit Client Login" : "New Client Login"}</div>
              <button onClick={() => setEditing(null)} data-testid="client-login-modal-close" className="h-9 w-9 border border-stone-300 flex items-center justify-center"><X size={15} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-[10px] font-bold uppercase text-stone-500 mb-1">Name</div>
                <input data-testid="cl-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} placeholder="e.g. John Smith" /></div>
              <div><div className="text-[10px] font-bold uppercase text-stone-500 mb-1">Username</div>
                <input data-testid="cl-username-input" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className={inp} disabled={!!editing.id} placeholder="e.g. jsmith" /></div>
              <div><div className="text-[10px] font-bold uppercase text-stone-500 mb-1">{editing.id ? "New Password (blank = keep)" : "Password"}</div>
                <input data-testid="cl-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inp} /></div>
              <div><div className="text-[10px] font-bold uppercase text-stone-500 mb-1">Link to Client (optional)</div>
                <select data-testid="cl-client-select" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className={inp}>
                  <option value="">— None —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-stone-500 mb-2">Visible Blocks — any mix across vineyards ({f.block_ids.length} selected)</div>
              <input data-testid="cl-block-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search vineyard, block, or variety…" className={`${inp} mb-2`} />
              <div className="space-y-3 max-h-72 overflow-y-auto border border-stone-200 p-3">
                {byRanch
                  .map(([label, blocks]) => {
                    const s = q.trim().toLowerCase();
                    if (!s) return [label, blocks];
                    if (label.toLowerCase().includes(s)) return [label, blocks];
                    const hit = blocks.filter((r) => `${r.block || ""} ${r.name || ""} ${r.variety || ""}`.toLowerCase().includes(s));
                    return [label, hit];
                  })
                  .filter(([, blocks]) => blocks.length > 0)
                  .map(([label, blocks]) => {
                    const allOn = blocks.every((r) => f.block_ids.includes(r.id));
                    return (
                      <div key={label}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="text-xs font-bold text-[#1F3D34]">{label}</div>
                          <button type="button" data-testid={`cl-ranch-all-${label}`} onClick={() => toggleRanch(blocks)}
                            className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${allOn ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "border-stone-300 text-stone-500"}`}>
                            {allOn ? "Clear All" : "Select All"}
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {blocks.map((r) => (
                            <button key={r.id} type="button" data-testid={`cl-block-${r.id}`} onClick={() => toggle(r.id)}
                              className={`h-9 px-3 text-xs font-semibold border ${f.block_ids.includes(r.id) ? "bg-[#7C9673] text-white border-[#7C9673]" : "bg-white border-stone-300"}`}>
                              {r.block || r.name}{r.variety ? ` · ${r.variety}` : ""}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
            {err && <div className="bg-red-50 border border-red-400 text-red-800 p-3 text-sm font-semibold" data-testid="cl-error">{err}</div>}
            <button data-testid="cl-save-btn" onClick={save} disabled={busy || !f.name.trim() || !f.username.trim() || (!editing.id && !f.password)}
              className="w-full h-12 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2">
              {busy && <Loader2 size={15} className="animate-spin" />} {editing.id ? "Save Changes" : "Create Login"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
